// Shared browser telemetry module (Application Insights).
//
// Lives outside any single app's src/ (Frontend/shared/, not e.g.
// forsuccessfrontend/src/telemetry/) because seven separate Vite apps import
// it unmodified: the portal (ForSuccessFrontend), ChellessAdmin,
// ProviderConsole, CustomerStatus, IbaApp, HomeAIHelper and LandingPage
// (story 18265). None of them has
// npm workspaces or a shared node_modules, so each app's vite.config.ts and
// tsconfig.app.json alias the bare specifier '@microsoft/applicationinsights-web'
// below back to that app's OWN node_modules — this file has no node_modules
// ancestor of its own, and without that alias the dynamic import a few lines
// down fails to resolve in a CI job that only ran `npm install` for one app.
// See Frontend/ProviderConsole/tsconfig.app.json's "paths" comment for the
// same pattern applied earlier to a shared React component (story 15402, D17).
//
// Inert by design: with no connection string configured, initTelemetry()
// returns before the SDK is ever imported, so an unconfigured build makes
// no network call and never touches the SDK's bundle at all. The optional
// `connectionString` override lets sibling SWA bundles (admin, provider,
// customer status, IBA, landing) reuse this module unmodified, each with its
// own distinctly-named env var (VITE_<APP>_APPLICATIONINSIGHTS_CONNECTION_STRING)
// so the bundles stay separable in Azure — see each app's .env.example.
//
// Story 18710: the same early-return gates on consent now, not only on the
// connection string. Browser Application Insights is non-essential tracking
// (Frontend/shared/consent's terms), so initTelemetry() makes no network
// call and never imports the SDK until hasConsent('non-essential') is true —
// gated here, once, rather than trusting seven importing apps to each ask first.
// onConsentChange re-runs the same check so a visitor who accepts mid-session
// (from the banner or the privacy page) gets telemetry started without a
// reload, using whatever connectionString/options the app originally passed.
//
// Follow-up to 18710: gating at this single module means EVERY importer is
// gated by default. `consent: 'not-required'` is the one explicit, reviewable
// way out, and it is only for a signed-in session. It is
// opt-IN and string-literal (not a boolean that defaults the wrong way), so a
// call site has to spell out the exemption to get it. Story 20562: it is
// called from a post-sign-in component (src/telemetry/SignedInTelemetry.tsx
// in ChellessAdmin, ProviderConsole and IbaApp), never from main.tsx, because
// an anonymous visitor reaches those apps' sign-in, sign-up and claim pages
// before any session exists. Exactly 3 call sites pass it: ChellessAdmin,
// IbaApp and ProviderConsole, each in src/telemetry/SignedInTelemetry.tsx.
// DEC-2026-09-22 reverses the IbaApp and ProviderConsole exemptions (banners
// required later, after Stripe), so treat those two as temporary.
// Frontend/shared/telemetry/consent-exemption.test.ts pins the allowlist.
//
// Story 18719: DEV and TEST each now share ONE Application Insights component
// across all seven bundles (standing policy forbids the Azure write a per-app
// resource would need), so bundles are no longer separable by which
// component they reported to. The optional `role` option stamps
// `ai.cloud.role` (via an addTelemetryInitializer callback, added right
// after loadAppInsights()) on every telemetry item instead, so
// `cloud_RoleName` is what separates the bundles in query results — one
// resource per environment, bundles separated by cloud role name.
import type { ApplicationInsights, ITelemetryItem } from '@microsoft/applicationinsights-web';
import { deleteCookies, hasConsent, onConsentChange } from '../consent';
import { TELEMETRY_COOKIE_NAMES } from './cookie-names';

export { TELEMETRY_COOKIE_NAMES } from './cookie-names';
export type { TelemetryCookieName } from './cookie-names';

export interface InitTelemetryOptions {
  connectionString?: string;
  /**
   * Explicit, opt-in escape from the consent gate below. Only for a
   * signed-in session; call it from the post-sign-in component, never from
   * main.tsx (see the story-18710 follow-up comment above this interface).
   * Omit this field entirely to stay gated (the correct default for
   * everything else, including any app added later).
   */
  consent?: 'not-required';
  /**
   * Stamps `ai.cloud.role` on every telemetry item so bundles sharing one
   * Application Insights component (story 18719) stay separable by
   * `cloud_RoleName` in query results.
   */
  role?: string;
  /**
   * Story 20562: applied to every URL-bearing field of every telemetry item
   * after the built-in redaction of `token` and `invite` query values, so an
   * app can strip its own path credentials (e.g. /s/:token) before they leave
   * the browser. Build it with redactCredentialUrl.
   */
  scrubUrl?: (url: string) => string;
}

export interface CredentialUrlRules {
  /** Path prefixes whose next segment is a credential, e.g. '/s/'. */
  pathPrefixes?: string[];
  /** Query parameter names whose value is a credential. */
  queryParams?: string[];
}

const REDACTED = '[redacted]';

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/**
 * Replaces the path segment after each prefix and the value of each named
 * query parameter with [redacted]. Works on absolute and relative URLs, and on
 * dependency names like "GET /s/abc". Never throws; input it cannot read is
 * returned unchanged.
 */
export function redactCredentialUrl(url: string, rules: CredentialUrlRules): string {
  try {
    if (typeof url !== 'string' || url === '') {
      return url;
    }
    let result = url;
    for (const prefix of rules.pathPrefixes ?? []) {
      if (!prefix) continue;
      // (?<!:/) keeps a host that happens to be named like the prefix ("https://s/") intact.
      result = result.replace(
        new RegExp(`(?<!:/)(${escapeRegExp(prefix)})[^/?#\\s]+`, 'gi'),
        `$1${REDACTED}`,
      );
    }
    for (const name of rules.queryParams ?? []) {
      if (!name) continue;
      result = result.replace(
        new RegExp(`([?&]${escapeRegExp(name)}=)[^&#\\s]*`, 'g'),
        `$1${REDACTED}`,
      );
    }
    return result;
  } catch {
    return url;
  }
}

const ALWAYS_REDACTED_QUERY_PARAMS = ['token', 'invite'];
// Story 25110: /share/<token> is a never-expiring bearer key, so every app redacts it. The
// reminder opt-out route carries its own token one segment deeper, so it must run first.
const ALWAYS_REDACTED_PATH_PREFIXES = ['/share/reminders/opt-out/', '/share/'];
export const SHARE_PATH_PLACEHOLDER = `/share/${REDACTED}`;
export const SHARE_PAGE_VIEW_NAME = 'Shared deliverable';
const SHARE_PATH = /\/share\/[^/?#\s]/i;
const SHARE_EVENT_NAMES = ['share_created', 'public_page_viewed', 'public_signup_clicked'];
const IDENTITY_TAGS = ['ai.user.authUserId', 'ai.user.accountId'];
const IDENTITY_PROPERTY_KEYS = /^(email|profile_?id|user_?id|account_?id)$/i;
const PAGE_VIEW_BASE_TYPES = ['PageviewData', 'PageviewPerformanceData'];

function isSharePageItem(baseData: Record<string, unknown> | undefined): boolean {
  const candidates = [baseData?.uri, baseData?.url];
  if (candidates.some((value) => typeof value === 'string' && SHARE_PATH.test(value))) {
    return true;
  }
  return typeof window !== 'undefined' && SHARE_PATH.test(window.location.pathname);
}
const URL_BASE_DATA_FIELDS = ['uri', 'refUri', 'url', 'name', 'target', 'data'];

function scrubTelemetryItem(item: ITelemetryItem, scrubUrl?: (url: string) => string): void {
  const scrub = (value: string): string => {
    let out = redactCredentialUrl(value, {
      pathPrefixes: ALWAYS_REDACTED_PATH_PREFIXES,
      queryParams: ALWAYS_REDACTED_QUERY_PARAMS,
    });
    if (scrubUrl) {
      out = scrubUrl(out);
    }
    return out;
  };
  const baseData = item.baseData as Record<string, unknown> | undefined;
  const eventName = baseData?.name ?? item.name;
  const shareItem =
    isSharePageItem(baseData) || (typeof eventName === 'string' && SHARE_EVENT_NAMES.includes(eventName));
  if (baseData) {
    for (const field of URL_BASE_DATA_FIELDS) {
      const value = baseData[field];
      if (typeof value === 'string') {
        baseData[field] = scrub(value);
      }
    }
  }
  // The page-visit-time Metric carries the page URL as properties.PageUrl, and the SDK's custom
  // properties bag (item.data) can hold URLs too.
  for (const bag of [baseData?.properties, item.data]) {
    if (bag && typeof bag === 'object') {
      const record = bag as Record<string, unknown>;
      for (const key of Object.keys(record)) {
        const value = record[key];
        if (typeof value === 'string') {
          record[key] = scrub(value);
        }
      }
    }
  }
  // A failed request on a /share/ address can carry the token in an exception's message or stack.
  const exceptions = baseData?.exceptions;
  if (Array.isArray(exceptions)) {
    for (const exception of exceptions as Record<string, unknown>[]) {
      for (const field of ['message', 'stack']) {
        if (typeof exception[field] === 'string') {
          exception[field] = scrub(exception[field] as string);
        }
      }
      if (Array.isArray(exception.parsedStack)) {
        for (const frame of exception.parsedStack as Record<string, unknown>[]) {
          for (const field of ['fileName', 'assembly', 'method']) {
            if (typeof frame[field] === 'string') {
              frame[field] = scrub(frame[field] as string);
            }
          }
        }
      }
    }
  }
  // The page-visit-time metric names the previous page by its raw title, which on /share/ can be
  // the deliverable title; the SDK builds it before initializers run.
  const properties = baseData?.properties as Record<string, unknown> | undefined;
  if (
    properties &&
    typeof properties.PageName === 'string' &&
    typeof properties.PageUrl === 'string' &&
    properties.PageUrl.includes(SHARE_PATH_PLACEHOLDER)
  ) {
    properties.PageName = SHARE_PAGE_VIEW_NAME;
  }
  if (shareItem) {
    stripShareItemIdentity(item, baseData);
  }
  const operationName = item.tags?.['ai.operation.name'];
  if (item.tags && typeof operationName === 'string') {
    item.tags['ai.operation.name'] = scrub(operationName);
  }
  // SDK v3 keeps the operation name (the page path) in ext.trace.name until the channel
  // serializes it into the tag above, and the initializers run on the ext form.
  const trace = (item.ext as Record<string, unknown> | undefined)?.trace as Record<string, unknown> | undefined;
  if (trace && typeof trace.name === 'string') {
    trace.name = scrub(trace.name);
  }
}

// The deliverable title (document.title) must not reach the page-view name, and no profile id
// or email may ride on a share item, even if a later change sets an authenticated user context.
function stripShareItemIdentity(item: ITelemetryItem, baseData: Record<string, unknown> | undefined): void {
  if (baseData && typeof item.baseType === 'string' && PAGE_VIEW_BASE_TYPES.includes(item.baseType)) {
    baseData.name = SHARE_PAGE_VIEW_NAME;
  }
  if (item.tags) {
    for (const tag of IDENTITY_TAGS) {
      delete item.tags[tag];
    }
  }
  const user = (item.ext as Record<string, unknown> | undefined)?.user as Record<string, unknown> | undefined;
  if (user) {
    delete user.authId;
  }
  for (const bag of [baseData?.properties, item.data]) {
    if (bag && typeof bag === 'object') {
      const record = bag as Record<string, unknown>;
      for (const key of Object.keys(record)) {
        if (IDENTITY_PROPERTY_KEYS.test(key)) {
          delete record[key];
        }
      }
    }
  }
}

let appInsights: ApplicationInsights | undefined;
let initializing = false;
let pendingStart: (() => void) | undefined;
let consentSubscribed = false;
let clientIsGated = false;
let errorListener: ((event: ErrorEvent) => void) | undefined;

function readConnectionString(override?: string): string {
  const value = override ?? import.meta.env.VITE_APPLICATIONINSIGHTS_CONNECTION_STRING;
  return typeof value === 'string' ? value.trim() : '';
}

// Returns a promise that settles once initialization has finished (or was
// skipped), so a caller that needs to observe the result - a test, chiefly -
// can await it instead of polling isTelemetryEnabled(). Loading the
// Application Insights SDK is a heavy dynamic import that on a loaded CI
// agent regularly outran vitest's 1s vi.waitFor default, which is exactly
// the intermittent frontend-pr-validation failure recorded in work item
// 18417. Every app call site ignores the return value and is unaffected:
// the promise never rejects (the .catch below swallows load failures).
export function initTelemetry(options?: InitTelemetryOptions): Promise<void> {
  if (appInsights || initializing) {
    return Promise.resolve();
  }

  // Gated callers remember how to start and subscribe once, on every path,
  // so a later accept starts telemetry and a later reject stops it (story
  // 19758). `consent: 'not-required'` (see InitTelemetryOptions above) skips
  // this entirely: the three apps that pass it (see the header) have no banner
  // and stay on for the whole session. The subscription sits above the
  // missing-connection-string return so a reject still clears tracking
  // cookies for a gated caller that never loaded a client (story 20564). The
  // start closure only exists when a connection string resolved: the callback
  // never names initTelemetry directly, so an unconfigured build can still
  // prove the SDK import unreachable (story 21340).
  const gated = options?.consent !== 'not-required';
  const connectionString = readConnectionString(options?.connectionString);
  if (gated) {
    pendingStart = connectionString ? () => void initTelemetry(options) : undefined;
    if (!consentSubscribed) {
      consentSubscribed = true;
      onConsentChange(() => {
        if (hasConsent('non-essential')) {
          pendingStart?.();
        } else {
          stopTelemetry();
        }
      });
    }
  }

  if (!connectionString) {
    return Promise.resolve();
  }
  if (gated && !hasConsent('non-essential')) {
    return Promise.resolve();
  }

  initializing = true;
  const role = options?.role;

  return import('@microsoft/applicationinsights-web')
    .then(({ ApplicationInsights }) => {
      // Another call may have completed while the dynamic import was in flight.
      if (appInsights) {
        return;
      }
      // Consent may have been withdrawn while the import was in flight.
      if (gated && !hasConsent('non-essential')) {
        return;
      }

      const client = new ApplicationInsights({
        config: {
          connectionString,
          autoTrackPageVisitTime: true,
          enableAutoRouteTracking: true,
          enableUnhandledPromiseRejectionTracking: true,
        },
      });
      client.loadAppInsights();

      const scrubUrl = options?.scrubUrl;
      client.addTelemetryInitializer((item: ITelemetryItem) => {
        try {
          scrubTelemetryItem(item, scrubUrl);
        } catch {
          // Telemetry must never break the app.
        }
      });

      if (role) {
        client.addTelemetryInitializer((item: ITelemetryItem) => {
          item.tags = item.tags ?? {};
          item.tags['ai.cloud.role'] = role;
        });
      }

      appInsights = client;
      clientIsGated = gated;

      errorListener = (event) => {
        appInsights?.trackException({ exception: event.error ?? new Error(event.message) });
      };
      window.addEventListener('error', errorListener);

      // enableAutoRouteTracking only records history changes, so the initial load needs an explicit page view.
      try {
        client.trackPageView();
      } catch {
        // Telemetry must never break the app; state above is already consistent.
      }
    })
    .catch(() => {
      // Telemetry must never break the app; swallow load/init failures.
    })
    .finally(() => {
      initializing = false;
    });
}

// Story 20564: the SDK's unload leaves its cookies behind (ai_user is a
// persistent device id), so withdrawal removes every TELEMETRY_COOKIE_NAMES entry.
function clearTelemetryStorage(): void {
  deleteCookies((name) => (TELEMETRY_COOKIE_NAMES as readonly string[]).includes(name));
  for (const storage of ['localStorage', 'sessionStorage'] as const) {
    try {
      const area = window[storage];
      for (const key of Object.keys(area)) {
        if (key.startsWith('AI_')) {
          area.removeItem(key);
        }
      }
    } catch {
      // Storage can throw (disabled, sandboxed); cleanup is best-effort.
    }
  }
}

function stopTelemetry(): void {
  if (!appInsights || !clientIsGated) {
    // No client loaded yet, but cookies may exist from an earlier page load.
    if (!appInsights && consentSubscribed) {
      clearTelemetryStorage();
    }
    return;
  }
  const client = appInsights;
  appInsights = undefined;
  if (errorListener) {
    window.removeEventListener('error', errorListener);
    errorListener = undefined;
  }
  try {
    // Stop the SDK re-writing cookies while it unloads.
    client.getCookieMgr?.()?.setEnabled(false);
  } catch {
    // Best-effort; the explicit cleanup below does not depend on it.
  }
  clearTelemetryStorage();
  try {
    void client.unload(true, () => clearTelemetryStorage());
  } catch {
    // Telemetry must never break the app; the client is already detached.
  }
}

export function trackEvent(name: string, properties?: Record<string, unknown>): void {
  try {
    appInsights?.trackEvent({ name }, properties);
  } catch {
    // no-op: telemetry failures must never surface to the caller
  }
}

export function trackError(error: unknown, properties?: Record<string, unknown>): void {
  try {
    const exception = error instanceof Error ? error : new Error(String(error));
    appInsights?.trackException({ exception }, properties);
  } catch {
    // no-op: telemetry failures must never surface to the caller
  }
}

export function isTelemetryEnabled(): boolean {
  return appInsights !== undefined;
}
